import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { useCompany } from "../contexts/CompanyContext";
import MessagingPanel from "../components/MessagingPanel";
import NotificationPanel from "../components/NotificationPanel";
import { useUnreadMessages } from "../hooks/useUnreadMessages";
import { useNotifications } from "../hooks/useNotifications";
import { subscribeToPush, isSubscribedToPush, isPushSupported, getNotificationPermission } from "../utils/pushNotifications";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { format, isToday, isTomorrow, parseISO } from "date-fns";
import { fr } from "date-fns/locale";
import {
  Calendar, MapPin, Phone, User, Clock, CheckCircle2,
  XCircle, AlertCircle, LogOut, ChevronRight, Navigation, FileText, CalendarPlus,
  Euro, CalendarCheck, XOctagon, Loader2, MessageCircle, Bell, BellRing, Plus
} from "lucide-react";
import { Calendar as CalendarPicker } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper to generate ICS file content
const generateICS = (appointment) => {
  const startDate = parseISO(`${appointment.date}T${appointment.time}:00`);
  const duration = appointment.duration_minutes || 60;
  const endDate = new Date(startDate.getTime() + duration * 60000);
  
  const formatDateICS = (date) => {
    return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
  };
  
  const address = `${appointment.customer_address}, ${appointment.customer_city}`;
  
  const icsContent = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//Vanessa//RDV//FR
BEGIN:VEVENT
UID:${appointment.id}@vanessa
DTSTAMP:${formatDateICS(new Date())}
DTSTART:${formatDateICS(startDate)}
DTEND:${formatDateICS(endDate)}
SUMMARY:RDV ${appointment.customer_name} - ${appointment.purpose}
DESCRIPTION:Client: ${appointment.customer_name}\\nTéléphone: ${appointment.customer_phone}\\nObjet: ${appointment.purpose}${appointment.teleprospector_notes ? '\\nNotes: ' + appointment.teleprospector_notes : ''}
LOCATION:${address}
END:VEVENT
END:VCALENDAR`;

  return icsContent;
};

// Helper to add to calendar (opens native calendar app)
const addToCalendar = (appointment) => {
  const icsContent = generateICS(appointment);
  const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
  const fileName = `rdv-${appointment.customer_name.replace(/\s+/g, '-')}.ics`;
  
  // Check if on mobile and Web Share API is available with file support
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  
  if (isMobile && navigator.canShare && navigator.canShare({ files: [new File([blob], fileName, { type: 'text/calendar' })] })) {
    // Use Web Share API on mobile for better integration
    const file = new File([blob], fileName, { type: 'text/calendar' });
    navigator.share({
      files: [file],
      title: `RDV ${appointment.customer_name}`,
      text: `Rendez-vous le ${appointment.date} à ${appointment.time}`
    }).then(() => {
      toast.success("Fichier partagé !");
    }).catch((err) => {
      console.log("Share failed, falling back to download", err);
      downloadICSFile(blob, fileName);
    });
  } else {
    downloadICSFile(blob, fileName);
  }
};

const downloadICSFile = (blob, fileName) => {
  // Create a data URL for better iOS compatibility
  const reader = new FileReader();
  reader.onloadend = () => {
    const link = document.createElement('a');
    link.href = reader.result;
    link.download = fileName;
    link.style.display = 'none';
    document.body.appendChild(link);
    
    // Use setTimeout for iOS Safari compatibility
    setTimeout(() => {
      link.click();
      document.body.removeChild(link);
      toast.success("Fichier téléchargé - Ouvrez-le pour l'ajouter à votre agenda");
    }, 100);
  };
  reader.readAsDataURL(blob);
};

const SalesRepDashboard = () => {
  const { user, logout } = useAuth();
  const { companyId } = useCompany();
  const { unreadCount, refreshUnreadCount } = useUnreadMessages();
  const { 
    notifications, 
    unreadCount: notifUnreadCount, 
    connected: wsConnected,
    markAsRead: markNotifAsRead,
    markAllAsRead: markAllNotifsAsRead
  } = useNotifications();
  const [showNotifications, setShowNotifications] = useState(false);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [visitNotes, setVisitNotes] = useState("");
  const [updating, setUpdating] = useState(false);
  const [showMessaging, setShowMessaging] = useState(false);
  
  // Sales stats
  const [salesStats, setSalesStats] = useState(null);
  
  // Filter for "Tous" tab
  const [allFilter, setAllFilter] = useState("all"); // all, scheduled, completed, sale, no_show, canceled
  
  // Visit result modal states
  const [showResultModal, setShowResultModal] = useState(false);
  const [pendingResultAppointment, setPendingResultAppointment] = useState(null);
  const [visitResult, setVisitResult] = useState("");
  const [saleAmount, setSaleAmount] = useState("");
  const [resultNotes, setResultNotes] = useState("");
  const [submittingResult, setSubmittingResult] = useState(false);
  
  // Push notification state
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushSupported, setPushSupported] = useState(false);

  // Personal appointment modal state
  const [showPersonalAptModal, setShowPersonalAptModal] = useState(false);
  const [personalApt, setPersonalApt] = useState({
    date: null,
    time: "",
    city: "",
    purpose: "",
    notes: ""
  });
  const [savingPersonalApt, setSavingPersonalApt] = useState(false);

  // Check push notification status on mount
  useEffect(() => {
    setPushSupported(isPushSupported());
    const checkPushStatus = async () => {
      const subscribed = await isSubscribedToPush();
      setPushEnabled(subscribed);
    };
    checkPushStatus();
  }, []);

  // Enable push notifications
  const enablePushNotifications = async () => {
    const result = await subscribeToPush();
    if (result.success) {
      setPushEnabled(true);
      toast.success("Notifications push activées !");
    } else {
      if (result.error === 'Permission denied') {
        toast.error("Veuillez autoriser les notifications dans les paramètres de votre navigateur");
      } else {
        toast.error("Erreur lors de l'activation des notifications");
      }
    }
  };

  const fetchSalesStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/stats/my-sales`);
      setSalesStats(response.data);
    } catch (error) {
      console.error("Error fetching sales stats:", error);
    }
  }, []);

  const fetchAppointments = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/appointments`);
      setAppointments(response.data);
      
      // Check for completed appointments without results
      const pendingResults = response.data.filter(
        apt => apt.status === "completed" && !apt.visit_result
      );
      
      if (pendingResults.length > 0) {
        // Show modal for the first pending result
        setPendingResultAppointment(pendingResults[0]);
        setShowResultModal(true);
      }
    } catch (error) {
      console.error("Error fetching appointments:", error);
      toast.error("Erreur lors du chargement des rendez-vous");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAppointments();
    fetchSalesStats();
  }, [fetchAppointments, fetchSalesStats]);

  const submitVisitResult = async () => {
    if (!visitResult) {
      toast.error("Veuillez sélectionner un résultat");
      return;
    }
    
    if (visitResult === "sale" && (!saleAmount || parseFloat(saleAmount) <= 0)) {
      toast.error("Veuillez indiquer le montant de la vente");
      return;
    }

    setSubmittingResult(true);
    try {
      await axios.put(`${API}/appointments/${pendingResultAppointment.id}/result`, {
        visit_result: visitResult,
        sale_amount: visitResult === "sale" ? parseFloat(saleAmount) : null,
        visit_notes: resultNotes || null
      });
      
      toast.success(
        visitResult === "sale" 
          ? `Vente de ${parseFloat(saleAmount).toLocaleString('fr-FR')}€ HT enregistrée !` 
          : "Résultat enregistré"
      );
      
      setShowResultModal(false);
      setVisitResult("");
      setSaleAmount("");
      setResultNotes("");
      setPendingResultAppointment(null);
      fetchAppointments();
      fetchSalesStats(); // Refresh sales stats after recording a sale
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setSubmittingResult(false);
    }
  };

  // Create personal appointment
  const createPersonalAppointment = async () => {
    if (!personalApt.date || !personalApt.time || !personalApt.city) {
      toast.error("Veuillez remplir la date, l'heure et la ville");
      return;
    }
    
    setSavingPersonalApt(true);
    try {
      await axios.post(`${API}/appointments/personal`, {
        date: format(personalApt.date, "yyyy-MM-dd"),
        time: personalApt.time,
        city: personalApt.city,
        purpose: personalApt.purpose || "RDV personnel",
        notes: personalApt.notes
      });
      toast.success("RDV personnel ajouté");
      setShowPersonalAptModal(false);
      setPersonalApt({ date: null, time: "", city: "", purpose: "", notes: "" });
      fetchAppointments();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'ajout");
    } finally {
      setSavingPersonalApt(false);
    }
  };

  const todayAppointments = appointments.filter(
    (apt) => apt.status === "scheduled" && isToday(parseISO(apt.date))
  );

  const tomorrowAppointments = appointments.filter(
    (apt) => apt.status === "scheduled" && isTomorrow(parseISO(apt.date))
  );

  const upcomingAppointments = appointments.filter(
    (apt) => apt.status === "scheduled" && !isToday(parseISO(apt.date)) && !isTomorrow(parseISO(apt.date))
  );

  const pastAppointments = appointments.filter(
    (apt) => apt.status !== "scheduled"
  );

  // Filtered appointments for "Tous" tab
  const getFilteredAllAppointments = () => {
    switch (allFilter) {
      case "scheduled":
        return appointments.filter(apt => apt.status === "scheduled");
      case "completed":
        return appointments.filter(apt => apt.status === "completed");
      case "sale":
        return appointments.filter(apt => apt.visit_result === "sale");
      case "no_show":
        return appointments.filter(apt => apt.status === "no_show");
      case "canceled":
        return appointments.filter(apt => apt.status === "canceled");
      default:
        return appointments;
    }
  };

  const filteredAllAppointments = getFilteredAllAppointments();

  const openDetails = (appointment) => {
    setSelectedAppointment(appointment);
    setVisitNotes(appointment.visit_notes || "");
    setShowDetailsModal(true);
  };

  const updateAppointmentStatus = async (status) => {
    setUpdating(true);
    try {
      await axios.put(`${API}/appointments/${selectedAppointment.id}`, {
        status,
        visit_notes: visitNotes
      });
      toast.success(
        status === "completed" ? "Rendez-vous marqué comme terminé" :
        status === "no_show" ? "Marqué comme absence" :
        "Rendez-vous annulé"
      );
      setShowDetailsModal(false);
      fetchAppointments();
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    } finally {
      setUpdating(false);
    }
  };

  const openGPS = (address, city) => {
    const query = encodeURIComponent(`${address}, ${city}`);
    window.open(`https://www.google.com/maps/search/?api=1&query=${query}`, "_blank");
  };

  const getStatusBadge = (status, visitResult) => {
    if (visitResult === "sale") {
      return { label: "Vendu", className: "bg-emerald-100 text-emerald-700 border-emerald-200" };
    }
    const badges = {
      scheduled: { label: "À faire", className: "bg-blue-100 text-blue-700 border-blue-200" },
      completed: { label: "Terminé", className: "bg-emerald-100 text-emerald-700 border-emerald-200" },
      no_show: { label: "Absence", className: "bg-amber-100 text-amber-700 border-amber-200" },
      canceled: { label: "Annulé", className: "bg-slate-100 text-slate-700 border-slate-200" }
    };
    return badges[status] || badges.scheduled;
  };

  const AppointmentCard = ({ appointment, showDate = false }) => (
    <Card 
      className="border-slate-100 shadow-sm mb-4 cursor-pointer hover:shadow-md transition-shadow"
      onClick={() => openDetails(appointment)}
      data-testid={`appointment-card-${appointment.id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
              <User className="w-5 h-5 text-indigo-600" strokeWidth={1.5} />
            </div>
            <div>
              <p className="font-semibold text-slate-900">{appointment.customer_name}</p>
              <p className="text-sm text-slate-500">{appointment.purpose}</p>
            </div>
          </div>
          <Badge className={`${getStatusBadge(appointment.status, appointment.visit_result).className} border`}>
            {getStatusBadge(appointment.status, appointment.visit_result).label}
          </Badge>
        </div>

        <div className="space-y-2">
          {showDate && (
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <Calendar className="w-4 h-4 text-slate-400" strokeWidth={1.5} />
              <span>{format(parseISO(appointment.date), "EEEE d MMMM", { locale: fr })}</span>
            </div>
          )}
          
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <Clock className="w-4 h-4 text-slate-400" strokeWidth={1.5} />
            <span>{appointment.time}</span>
          </div>

          <button
            onClick={(e) => {
              e.stopPropagation();
              openGPS(appointment.customer_address, appointment.customer_city);
            }}
            className="flex items-center gap-2 text-sm text-indigo-600 hover:text-indigo-700"
            data-testid={`gps-btn-${appointment.id}`}
          >
            <MapPin className="w-4 h-4" strokeWidth={1.5} />
            <span>{appointment.customer_address}, {appointment.customer_city}</span>
            <Navigation className="w-3 h-3" />
          </button>

          <div className="flex items-center gap-2 text-sm text-slate-600">
            <Phone className="w-4 h-4 text-slate-400" strokeWidth={1.5} />
            <a 
              href={`tel:${appointment.customer_phone}`}
              onClick={(e) => e.stopPropagation()}
              className="text-indigo-600 hover:text-indigo-700 font-mono"
            >
              {appointment.customer_phone}
            </a>
          </div>
        </div>

        {appointment.teleprospector_notes && (
          <div className="mt-3 pt-3 border-t border-slate-100">
            <div className="flex items-start gap-2">
              <FileText className="w-4 h-4 text-slate-400 mt-0.5" strokeWidth={1.5} />
              <p className="text-sm text-slate-500">{appointment.teleprospector_notes}</p>
            </div>
          </div>
        )}

        <div className="mt-3 flex justify-end">
          <ChevronRight className="w-5 h-5 text-slate-400" />
        </div>
      </CardContent>
    </Card>
  );

  const EmptyState = ({ message }) => (
    <div className="text-center py-12">
      <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-4" />
      <p className="text-slate-500">{message}</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      {/* Header - with safe area padding for mobile PWA */}
      <header 
        className="bg-gradient-to-r from-indigo-600 to-indigo-700 text-white px-4 pb-4 sticky top-0 z-10 shadow-lg"
        style={{ 
          paddingTop: 'max(env(safe-area-inset-top, 0px), 44px)',
        }}
      >
        <div className="max-w-lg mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-white" strokeWidth={1.5} />
              </div>
              <div>
                <h1 className="text-lg font-bold font-['Manrope'] text-white">Mes RDV</h1>
                <p className="text-sm text-indigo-200">{user?.name}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Add personal appointment button */}
              <button
                onClick={() => setShowPersonalAptModal(true)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-white transition-colors"
                title="Ajouter un RDV personnel"
              >
                <CalendarPlus className="w-5 h-5" />
                <span className="text-sm font-medium hidden sm:inline">+ RDV</span>
              </button>
              <button
                onClick={() => setShowMessaging(true)}
                className="relative flex items-center gap-2 px-3 py-2 rounded-lg bg-white/20 hover:bg-white/30 text-white transition-colors"
                title="Messages"
              >
                <MessageCircle className="w-5 h-5" />
                <span className="text-sm font-medium">Messages</span>
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold animate-pulse">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>
              <button
                onClick={() => setShowNotifications(true)}
                className="relative flex items-center justify-center w-10 h-10 rounded-lg bg-white/20 hover:bg-white/30 text-white transition-colors"
                title="Notifications"
              >
                <Bell className="w-5 h-5" />
                {notifUnreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-amber-500 text-white text-xs rounded-full flex items-center justify-center font-bold animate-pulse">
                    {notifUnreadCount > 9 ? '9+' : notifUnreadCount}
                  </span>
                )}
                {wsConnected && (
                  <span className="absolute bottom-0 right-0 w-2 h-2 bg-emerald-400 rounded-full border border-white pointer-events-none" />
                )}
              </button>
              {/* Push notifications toggle */}
              {pushSupported && !pushEnabled && (
                <button
                  onClick={enablePushNotifications}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-white transition-colors animate-pulse"
                  title="Activer les notifications push"
                >
                  <BellRing className="w-5 h-5" />
                  <span className="text-sm font-medium hidden sm:inline">Activer</span>
                </button>
              )}
              {pushEnabled && (
                <span className="flex items-center gap-1 px-2 py-1 bg-emerald-500/20 text-emerald-300 rounded-lg text-xs">
                  <BellRing className="w-3 h-3" />
                  Push actif
                </span>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={logout}
                data-testid="logout-btn"
                className="text-white hover:bg-white/20"
              >
                <LogOut className="w-5 h-5" />
              </Button>
            </div>
          </div>
          
          {/* Monthly Sales Stats Card */}
          {salesStats && (
            <div className="mt-4 bg-white/10 backdrop-blur rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-indigo-200">Ventes du mois</p>
                  <p className="text-2xl font-bold text-white">
                    {salesStats.monthly_revenue.toLocaleString('fr-FR')} € HT
                  </p>
                </div>
                <div className="text-right">
                  <div className="flex items-center gap-1 text-indigo-200">
                    <Euro className="w-4 h-4" />
                    <span className="text-sm">{salesStats.monthly_sales_count} vente{salesStats.monthly_sales_count !== 1 ? 's' : ''}</span>
                  </div>
                  {salesStats.conversion_rate > 0 && (
                    <p className="text-xs text-indigo-300 mt-1">
                      Taux: {salesStats.conversion_rate.toFixed(0)}%
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Push notification invitation banner */}
      {pushSupported && !pushEnabled && (
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-4 py-3">
          <div className="max-w-4xl mx-auto flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 text-white">
              <BellRing className="w-5 h-5" />
              <p className="text-sm">
                <span className="font-medium">Activez les notifications</span> pour être alerté de vos nouveaux RDV même quand l'app est fermée
              </p>
            </div>
            <button
              onClick={enablePushNotifications}
              className="px-4 py-2 bg-white text-amber-600 rounded-lg font-medium text-sm hover:bg-amber-50 transition-colors flex-shrink-0"
            >
              Activer maintenant
            </button>
          </div>
        </div>
      )}

      {/* Messaging Panel */}
      <MessagingPanel 
        isOpen={showMessaging} 
        onClose={() => {
          setShowMessaging(false);
          refreshUnreadCount();
        }} 
      />

      {/* Notification Panel */}
      <NotificationPanel
        isOpen={showNotifications}
        onClose={() => setShowNotifications(false)}
        notifications={notifications}
        unreadCount={notifUnreadCount}
        onMarkAsRead={markNotifAsRead}
        onMarkAllAsRead={markAllNotifsAsRead}
        connected={wsConnected}
      />

      {/* Content */}
      <div className="max-w-lg mx-auto px-4 pt-4">
        <Tabs defaultValue="today" className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-4">
            <TabsTrigger value="today" data-testid="tab-today">
              Aujourd'hui
              {todayAppointments.length > 0 && (
                <Badge className="ml-2 bg-indigo-100 text-indigo-700">{todayAppointments.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="tomorrow" data-testid="tab-tomorrow">
              Demain
              {tomorrowAppointments.length > 0 && (
                <Badge className="ml-2 bg-slate-100 text-slate-700">{tomorrowAppointments.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="all" data-testid="tab-all">Tous</TabsTrigger>
          </TabsList>

          <TabsContent value="today" className="mt-0">
            {loading ? (
              <div className="text-center py-12 text-slate-500">Chargement...</div>
            ) : todayAppointments.length === 0 ? (
              <EmptyState message="Aucun rendez-vous aujourd'hui" />
            ) : (
              todayAppointments.map((apt) => (
                <AppointmentCard key={apt.id} appointment={apt} />
              ))
            )}
          </TabsContent>

          <TabsContent value="tomorrow" className="mt-0">
            {loading ? (
              <div className="text-center py-12 text-slate-500">Chargement...</div>
            ) : tomorrowAppointments.length === 0 ? (
              <EmptyState message="Aucun rendez-vous demain" />
            ) : (
              tomorrowAppointments.map((apt) => (
                <AppointmentCard key={apt.id} appointment={apt} />
              ))
            )}
          </TabsContent>

          <TabsContent value="all" className="mt-0">
            {/* Filters */}
            <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
              <button
                onClick={() => setAllFilter("all")}
                className={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors ${
                  allFilter === "all" 
                    ? "bg-indigo-600 text-white" 
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                Tous ({appointments.length})
              </button>
              <button
                onClick={() => setAllFilter("scheduled")}
                className={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors ${
                  allFilter === "scheduled" 
                    ? "bg-blue-600 text-white" 
                    : "bg-blue-50 text-blue-700 hover:bg-blue-100"
                }`}
              >
                À faire ({appointments.filter(a => a.status === "scheduled").length})
              </button>
              <button
                onClick={() => setAllFilter("completed")}
                className={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors ${
                  allFilter === "completed" 
                    ? "bg-slate-600 text-white" 
                    : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                }`}
              >
                Terminé ({appointments.filter(a => a.status === "completed").length})
              </button>
              <button
                onClick={() => setAllFilter("sale")}
                className={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors ${
                  allFilter === "sale" 
                    ? "bg-emerald-600 text-white" 
                    : "bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                }`}
              >
                Vendu ({appointments.filter(a => a.visit_result === "sale").length})
              </button>
              <button
                onClick={() => setAllFilter("no_show")}
                className={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors ${
                  allFilter === "no_show" 
                    ? "bg-amber-600 text-white" 
                    : "bg-amber-50 text-amber-700 hover:bg-amber-100"
                }`}
              >
                Absence ({appointments.filter(a => a.status === "no_show").length})
              </button>
              <button
                onClick={() => setAllFilter("canceled")}
                className={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors ${
                  allFilter === "canceled" 
                    ? "bg-slate-600 text-white" 
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                Annulé ({appointments.filter(a => a.status === "canceled").length})
              </button>
            </div>

            {loading ? (
              <div className="text-center py-12 text-slate-500">Chargement...</div>
            ) : filteredAllAppointments.length === 0 ? (
              <EmptyState message="Aucun rendez-vous trouvé" />
            ) : (
              <div>
                {filteredAllAppointments.map((apt) => (
                  <AppointmentCard key={apt.id} appointment={apt} showDate />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Details Modal */}
      <Dialog open={showDetailsModal} onOpenChange={setShowDetailsModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">Détails du rendez-vous</DialogTitle>
          </DialogHeader>
          
          {selectedAppointment && (
            <div className="space-y-4 py-4">
              <div className="flex items-center gap-3 pb-4 border-b border-slate-100">
                <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center">
                  <User className="w-6 h-6 text-indigo-600" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="font-semibold text-slate-900">{selectedAppointment.customer_name}</p>
                  <p className="text-sm text-slate-500">{selectedAppointment.purpose}</p>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Calendar className="w-5 h-5 text-slate-400" strokeWidth={1.5} />
                  <span className="text-slate-700">
                    {format(parseISO(selectedAppointment.date), "EEEE d MMMM yyyy", { locale: fr })}
                  </span>
                </div>

                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-slate-400" strokeWidth={1.5} />
                  <span className="text-slate-700">{selectedAppointment.time}</span>
                </div>

                <button
                  onClick={() => openGPS(selectedAppointment.customer_address, selectedAppointment.customer_city)}
                  className="flex items-center gap-3 text-indigo-600 hover:text-indigo-700 w-full text-left"
                  data-testid="modal-gps-btn"
                >
                  <MapPin className="w-5 h-5" strokeWidth={1.5} />
                  <span>{selectedAppointment.customer_address}, {selectedAppointment.customer_city}</span>
                  <Navigation className="w-4 h-4 ml-auto" />
                </button>

                <a 
                  href={`tel:${selectedAppointment.customer_phone}`}
                  className="flex items-center gap-3 text-indigo-600 hover:text-indigo-700"
                >
                  <Phone className="w-5 h-5" strokeWidth={1.5} />
                  <span className="font-mono">{selectedAppointment.customer_phone}</span>
                </a>
              </div>

              {selectedAppointment.teleprospector_notes && (
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Notes du téléprospecteur</p>
                  <p className="text-sm text-slate-700">{selectedAppointment.teleprospector_notes}</p>
                </div>
              )}

              {/* Add to Calendar Button */}
              <Button
                onClick={() => addToCalendar(selectedAppointment)}
                variant="outline"
                className="w-full border-indigo-200 text-indigo-700 hover:bg-indigo-50"
              >
                <CalendarPlus className="w-4 h-4 mr-2" />
                Ajouter à mon calendrier
              </Button>

              {selectedAppointment.status === "scheduled" && (
                <div>
                  <Label className="text-slate-700">Notes de visite</Label>
                  <Textarea
                    value={visitNotes}
                    onChange={(e) => setVisitNotes(e.target.value)}
                    data-testid="visit-notes"
                    placeholder="Ajouter des notes sur la visite..."
                    className="mt-1"
                    rows={3}
                  />
                </div>
              )}
            </div>
          )}

          <DialogFooter className="flex-col gap-2 sm:flex-col">
            {selectedAppointment?.status === "scheduled" && (
              <>
                <Button
                  onClick={() => updateAppointmentStatus("completed")}
                  disabled={updating}
                  data-testid="mark-completed-btn"
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                >
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Marquer comme terminé
                </Button>
                <div className="flex gap-2 w-full">
                  <Button
                    onClick={() => updateAppointmentStatus("no_show")}
                    disabled={updating}
                    data-testid="mark-noshow-btn"
                    variant="outline"
                    className="flex-1 border-amber-200 text-amber-700 hover:bg-amber-50"
                  >
                    <AlertCircle className="w-4 h-4 mr-2" />
                    Absence
                  </Button>
                  <Button
                    onClick={() => updateAppointmentStatus("canceled")}
                    disabled={updating}
                    data-testid="mark-canceled-btn"
                    variant="outline"
                    className="flex-1 border-slate-200 text-slate-600 hover:bg-slate-50"
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Annuler
                  </Button>
                </div>
              </>
            )}
            <Button
              variant="ghost"
              onClick={() => setShowDetailsModal(false)}
              className="w-full"
            >
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Visit Result Modal - Mandatory before accessing other content */}
      <Dialog open={showResultModal} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-md" onPointerDownOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="font-['Manrope'] text-center">
              Comment s'est passé le rendez-vous ?
            </DialogTitle>
          </DialogHeader>
          
          {pendingResultAppointment && (
            <div className="space-y-4 py-4">
              {/* Client info */}
              <div className="bg-slate-50 rounded-lg p-3 text-center">
                <p className="font-medium text-slate-900">{pendingResultAppointment.customer_name}</p>
                <p className="text-sm text-slate-500">
                  {format(parseISO(pendingResultAppointment.date), "EEEE d MMMM", { locale: fr })} à {pendingResultAppointment.time}
                </p>
              </div>

              {/* Result options */}
              <div className="space-y-3">
                {/* Option 1: Sale */}
                <div
                  onClick={() => setVisitResult("sale")}
                  className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    visitResult === "sale"
                      ? "border-emerald-500 bg-emerald-50"
                      : "border-slate-200 hover:border-slate-300"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      visitResult === "sale" ? "bg-emerald-100" : "bg-slate-100"
                    }`}>
                      <Euro className={`w-5 h-5 ${visitResult === "sale" ? "text-emerald-600" : "text-slate-500"}`} />
                    </div>
                    <div className="flex-1">
                      <p className={`font-medium ${visitResult === "sale" ? "text-emerald-700" : "text-slate-700"}`}>
                        J'ai vendu une prestation
                      </p>
                      <p className="text-sm text-slate-500">Prestation complémentaire ou vente</p>
                    </div>
                    {visitResult === "sale" && (
                      <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                    )}
                  </div>
                </div>

                {/* Sale amount input */}
                {visitResult === "sale" && (
                  <div className="pl-4 border-l-2 border-emerald-300 ml-5">
                    <Label className="text-slate-700">Montant de la vente (€ HT) *</Label>
                    <div className="relative mt-1">
                      <Input
                        type="number"
                        value={saleAmount}
                        onChange={(e) => setSaleAmount(e.target.value)}
                        placeholder="0.00"
                        className="pr-8"
                        autoFocus
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">€</span>
                    </div>
                  </div>
                )}

                {/* Option 2: New appointment */}
                <div
                  onClick={() => setVisitResult("new_appointment")}
                  className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    visitResult === "new_appointment"
                      ? "border-blue-500 bg-blue-50"
                      : "border-slate-200 hover:border-slate-300"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      visitResult === "new_appointment" ? "bg-blue-100" : "bg-slate-100"
                    }`}>
                      <CalendarCheck className={`w-5 h-5 ${visitResult === "new_appointment" ? "text-blue-600" : "text-slate-500"}`} />
                    </div>
                    <div className="flex-1">
                      <p className={`font-medium ${visitResult === "new_appointment" ? "text-blue-700" : "text-slate-700"}`}>
                        J'ai pris un autre rendez-vous
                      </p>
                      <p className="text-sm text-slate-500">Le client souhaite un nouveau RDV</p>
                    </div>
                    {visitResult === "new_appointment" && (
                      <CheckCircle2 className="w-5 h-5 text-blue-600" />
                    )}
                  </div>
                </div>

                {/* Option 3: No follow-up */}
                <div
                  onClick={() => setVisitResult("no_follow_up")}
                  className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    visitResult === "no_follow_up"
                      ? "border-slate-500 bg-slate-50"
                      : "border-slate-200 hover:border-slate-300"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      visitResult === "no_follow_up" ? "bg-slate-200" : "bg-slate-100"
                    }`}>
                      <XOctagon className={`w-5 h-5 ${visitResult === "no_follow_up" ? "text-slate-600" : "text-slate-500"}`} />
                    </div>
                    <div className="flex-1">
                      <p className={`font-medium ${visitResult === "no_follow_up" ? "text-slate-700" : "text-slate-700"}`}>
                        Pas de suite
                      </p>
                      <p className="text-sm text-slate-500">Le client n'est pas intéressé</p>
                    </div>
                    {visitResult === "no_follow_up" && (
                      <CheckCircle2 className="w-5 h-5 text-slate-600" />
                    )}
                  </div>
                </div>
              </div>

              {/* Optional notes */}
              <div>
                <Label className="text-slate-700">Notes (optionnel)</Label>
                <Textarea
                  value={resultNotes}
                  onChange={(e) => setResultNotes(e.target.value)}
                  placeholder="Commentaires sur le rendez-vous..."
                  className="mt-1"
                  rows={2}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              onClick={submitVisitResult}
              disabled={!visitResult || submittingResult || (visitResult === "sale" && !saleAmount)}
              className="w-full bg-indigo-600 hover:bg-indigo-700"
            >
              {submittingResult ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Enregistrement...
                </>
              ) : (
                "Valider"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Personal Appointment Modal */}
      <Dialog open={showPersonalAptModal} onOpenChange={setShowPersonalAptModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarPlus className="w-5 h-5 text-emerald-600" />
              Ajouter un RDV personnel
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-slate-500">
              Ce RDV sera visible par la téléprospectrice pour éviter les conflits d'agenda.
            </p>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-700">Date *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full mt-1 justify-start">
                      <Calendar className="mr-2 h-4 w-4" />
                      {personalApt.date ? format(personalApt.date, "d MMM", { locale: fr }) : "Choisir"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <CalendarPicker 
                      mode="single" 
                      selected={personalApt.date} 
                      onSelect={(d) => setPersonalApt({...personalApt, date: d})}
                      disabled={(date) => date < new Date(new Date().setHours(0,0,0,0))}
                      initialFocus 
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <Label className="text-slate-700">Heure *</Label>
                <Input
                  type="time"
                  value={personalApt.time}
                  onChange={(e) => setPersonalApt({...personalApt, time: e.target.value})}
                  className="mt-1"
                />
              </div>
            </div>

            <div>
              <Label className="text-slate-700">Ville *</Label>
              <Input
                value={personalApt.city}
                onChange={(e) => setPersonalApt({...personalApt, city: e.target.value})}
                placeholder="Ex: Lyon, Paris..."
                className="mt-1"
              />
              <p className="text-xs text-slate-400 mt-1">Permet à la téléprospectrice de savoir où vous serez</p>
            </div>

            <div>
              <Label className="text-slate-700">Motif</Label>
              <Input
                value={personalApt.purpose}
                onChange={(e) => setPersonalApt({...personalApt, purpose: e.target.value})}
                placeholder="Ex: Client existant, Réunion..."
                className="mt-1"
              />
            </div>

            <div>
              <Label className="text-slate-700">Notes (optionnel)</Label>
              <Textarea
                value={personalApt.notes}
                onChange={(e) => setPersonalApt({...personalApt, notes: e.target.value})}
                placeholder="Informations complémentaires..."
                className="mt-1"
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPersonalAptModal(false)}>Annuler</Button>
            <Button onClick={createPersonalAppointment} disabled={savingPersonalApt} className="bg-emerald-600 hover:bg-emerald-700">
              {savingPersonalApt ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Ajouter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SalesRepDashboard;
